import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from "react";
import { Calendar, ChevronLeft, ChevronRight, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { parseRegisterDate } from "@/lib/format";

export interface DatePickerProps {
  id?: string;
  value: string;
  onChange: (text: string, iso: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  required?: boolean;
  className?: string;
  "aria-label"?: string;
  minDate?: string; // ISO yyyy-mm-dd
  maxDate?: string; // ISO yyyy-mm-dd
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const WEEKDAY_NAMES = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

function padZero(num: number): string {
  return num.toString().padStart(2, "0");
}

function formatDateToParts(date: Date): { year: number; month: number; day: number; iso: string; registerText: string } {
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-indexed
  const day = date.getDate();
  const iso = `${year}-${padZero(month + 1)}-${padZero(day)}`;
  const registerText = `${padZero(day)}/${padZero(month + 1)}/${year}`;
  return { year, month, day, iso, registerText };
}

export function DatePicker({
  id,
  value,
  onChange,
  placeholder = "dd/mm/yyyy",
  disabled = false,
  invalid = false,
  required = false,
  className,
  "aria-label": ariaLabel,
  minDate,
  maxDate,
}: DatePickerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const calendarId = useId();

  const [open, setOpen] = useState(false);
  const [localText, setLocalText] = useState(value);

  useEffect(() => {
    setLocalText(value);
  }, [value]);

  // Parse current value to determine active date
  const parsedIso = useMemo(() => {
    if (!value) return null;
    return parseRegisterDate(value);
  }, [value]);

  const selectedParts = useMemo(() => {
    if (!parsedIso) return null;
    const [y, m, d] = parsedIso.split("-").map(Number);
    if (!y || !m || !d) return null;
    return { year: y, month: m - 1, day: d };
  }, [parsedIso]);

  // Today reference
  const today = useMemo(() => new Date(), []);
  const todayParts = useMemo(() => formatDateToParts(today), [today]);

  // Calendar view year and month (0-indexed)
  const [viewYear, setViewYear] = useState(() => selectedParts?.year ?? todayParts.year);
  const [viewMonth, setViewMonth] = useState(() => selectedParts?.month ?? todayParts.month);

  // Sync calendar view when selected date changes externally
  useEffect(() => {
    if (selectedParts) {
      setViewYear(selectedParts.year);
      setViewMonth(selectedParts.month);
    }
  }, [selectedParts]);

  // Close calendar on outside click or Escape
  useEffect(() => {
    if (!open) return;

    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function handleKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
        inputRef.current?.focus();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  // Generate days grid for current view month
  const calendarDays = useMemo(() => {
    const daysInCurrentMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    // Monday-first indexing: getDay() 0=Sun => 6, 1=Mon => 0, 2=Tue => 1, ...
    const firstDayOfWeek = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7;

    const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate();

    const days: Array<{
      day: number;
      month: number;
      year: number;
      isCurrentMonth: boolean;
      iso: string;
      registerText: string;
    }> = [];

    // Leading days from previous month
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const d = daysInPrevMonth - i;
      const prevM = viewMonth === 0 ? 11 : viewMonth - 1;
      const prevY = viewMonth === 0 ? viewYear - 1 : viewYear;
      const iso = `${prevY}-${padZero(prevM + 1)}-${padZero(d)}`;
      const registerText = `${padZero(d)}/${padZero(prevM + 1)}/${prevY}`;
      days.push({ day: d, month: prevM, year: prevY, isCurrentMonth: false, iso, registerText });
    }

    // Days in current month
    for (let d = 1; d <= daysInCurrentMonth; d++) {
      const iso = `${viewYear}-${padZero(viewMonth + 1)}-${padZero(d)}`;
      const registerText = `${padZero(d)}/${padZero(viewMonth + 1)}/${viewYear}`;
      days.push({ day: d, month: viewMonth, year: viewYear, isCurrentMonth: true, iso, registerText });
    }

    // Trailing days to fill complete grid rows (multiples of 7)
    const totalCurrent = days.length;
    const trailingCount = totalCurrent % 7 === 0 ? 0 : 7 - (totalCurrent % 7);
    for (let d = 1; d <= trailingCount; d++) {
      const nextM = viewMonth === 11 ? 0 : viewMonth + 1;
      const nextY = viewMonth === 11 ? viewYear + 1 : viewYear;
      const iso = `${nextY}-${padZero(nextM + 1)}-${padZero(d)}`;
      const registerText = `${padZero(d)}/${padZero(nextM + 1)}/${nextY}`;
      days.push({ day: d, month: nextM, year: nextY, isCurrentMonth: false, iso, registerText });
    }

    return days;
  }, [viewYear, viewMonth]);

  function handleSelectDate(registerText: string, iso: string, newYear: number, newMonth: number) {
    onChange(registerText, iso);
    setViewYear(newYear);
    setViewMonth(newMonth);
    setOpen(false);
  }

  function handlePrevMonth() {
    if (viewMonth === 0) {
      setViewYear((y) => y - 1);
      setViewMonth(11);
    } else {
      setViewMonth((m) => m - 1);
    }
  }

  function handleNextMonth() {
    if (viewMonth === 11) {
      setViewYear((y) => y + 1);
      setViewMonth(0);
    } else {
      setViewMonth((m) => m + 1);
    }
  }

  function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
    const text = event.target.value;
    setLocalText(text);
    if (!text.trim()) {
      onChange("", null);
      return;
    }
    const parsed = parseRegisterDate(text);
    onChange(text, parsed);
    if (parsed) {
      const [y, m] = parsed.split("-").map(Number);
      if (y && m) {
        setViewYear(y);
        setViewMonth(m - 1);
      }
    }
  }

  function handleInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown" && !open) {
      event.preventDefault();
      setOpen(true);
    }
  }

  function selectToday() {
    const { registerText, iso, year, month } = formatDateToParts(new Date());
    setLocalText(registerText);
    handleSelectDate(registerText, iso, year, month);
  }

  function selectYesterday() {
    const yesterday = new Date(Date.now() - 86_400_000);
    const { registerText, iso, year, month } = formatDateToParts(yesterday);
    setLocalText(registerText);
    handleSelectDate(registerText, iso, year, month);
  }

  function clearDate() {
    setLocalText("");
    onChange("", null);
    inputRef.current?.focus();
  }

  // Range of selectable years (e.g. 2015 to current year + 2)
  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    const startYear = Math.min(2015, (selectedParts?.year ?? current) - 5);
    const endYear = Math.max(current + 2, (selectedParts?.year ?? current) + 2);
    const years: number[] = [];
    for (let y = startYear; y <= endYear; y++) {
      years.push(y);
    }
    return years;
  }, [selectedParts]);

  return (
    <div ref={containerRef} className={cn("relative w-full", open && "z-40")}>
      <div className="relative flex items-center">
        <input
          ref={inputRef}
          id={id}
          type="text"
          inputMode="numeric"
          value={localText}
          onClick={() => setOpen(true)}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          aria-label={ariaLabel}
          aria-haspopup="dialog"
          aria-expanded={open}
          className={cn(
            "h-11 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 pr-20 cursor-pointer",
            "placeholder:text-slate-400 focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600",
            "disabled:bg-slate-100 disabled:text-slate-500",
            invalid && "border-blocking ring-1 ring-blocking",
            className,
          )}
        />

        <div className="absolute right-1 flex items-center gap-0.5">
          {Boolean(value) && !disabled && (
            <button
              type="button"
              onClick={clearDate}
              aria-label="Clear date"
              className="flex h-8 w-8 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none"
            >
              <X className="h-4 w-4" />
            </button>
          )}

          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            disabled={disabled}
            aria-label="Choose date from calendar"
            aria-controls={open ? calendarId : undefined}
            className={cn(
              "flex h-9 w-9 items-center justify-center rounded-md text-slate-500 transition-colors",
              "hover:bg-slate-100 hover:text-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500",
              open && "bg-brand-50 text-brand-600 ring-1 ring-brand-500",
            )}
          >
            <Calendar className="h-4 w-4" />
          </button>
        </div>
      </div>

      {open && (
        <div
          id={calendarId}
          role="dialog"
          aria-label="Calendar date picker"
          className="absolute left-0 z-50 mt-1.5 w-72 rounded-lg border border-slate-200 bg-white p-3 shadow-2xl ring-1 ring-black/10"
        >
          {/* Calendar Header with Navigation and Month/Year Dropdowns */}
          <div className="mb-2 flex items-center justify-between gap-1">
            <button
              type="button"
              onClick={handlePrevMonth}
              aria-label="Previous month"
              className="flex h-7 w-7 items-center justify-center rounded text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus:outline-none"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-1">
              <select
                value={viewMonth}
                onChange={(e) => setViewMonth(Number(e.target.value))}
                aria-label="Select month"
                className="rounded bg-transparent px-1.5 py-0.5 text-xs font-semibold text-slate-800 hover:bg-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-500 cursor-pointer"
              >
                {MONTH_NAMES.map((name, index) => (
                  <option key={name} value={index}>
                    {name}
                  </option>
                ))}
              </select>

              <select
                value={viewYear}
                onChange={(e) => setViewYear(Number(e.target.value))}
                aria-label="Select year"
                className="rounded bg-transparent px-1.5 py-0.5 text-xs font-semibold text-slate-800 hover:bg-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-500 cursor-pointer"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              onClick={handleNextMonth}
              aria-label="Next month"
              className="flex h-7 w-7 items-center justify-center rounded text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus:outline-none"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Weekdays header */}
          <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-medium text-slate-400 mb-1">
            {WEEKDAY_NAMES.map((name) => (
              <div key={name} className="py-1">
                {name}
              </div>
            ))}
          </div>

          {/* Days Grid */}
          <div className="grid grid-cols-7 gap-1" role="grid">
            {calendarDays.map((cell) => {
              const isSelected = parsedIso === cell.iso;
              const isToday = todayParts.iso === cell.iso;

              const isOutOfRange =
                (minDate && cell.iso < minDate) || (maxDate && cell.iso > maxDate);

              return (
                <button
                  key={cell.iso}
                  type="button"
                  disabled={Boolean(isOutOfRange)}
                  onClick={() =>
                    handleSelectDate(cell.registerText, cell.iso, cell.year, cell.month)
                  }
                  aria-label={`${cell.day} ${MONTH_NAMES[cell.month]} ${cell.year}`}
                  aria-selected={isSelected}
                  className={cn(
                    "flex h-8 w-8 items-center justify-center rounded-md text-xs transition-colors",
                    cell.isCurrentMonth
                      ? "text-slate-800 hover:bg-brand-50 hover:text-brand-900"
                      : "text-slate-300 hover:bg-slate-50 hover:text-slate-500",
                    isToday && !isSelected && "border border-brand-500 font-semibold text-brand-600",
                    isSelected &&
                      "!bg-brand-600 !text-white font-semibold shadow-sm hover:!bg-brand-700",
                    isOutOfRange && "opacity-25 pointer-events-none",
                  )}
                >
                  {cell.day}
                </button>
              );
            })}
          </div>

          {/* Quick selection footer */}
          <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-2 text-xs">
            <div className="flex gap-1">
              <button
                type="button"
                onClick={selectYesterday}
                className="rounded px-2 py-1 font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              >
                Yesterday
              </button>
              <button
                type="button"
                onClick={selectToday}
                className="rounded px-2 py-1 font-semibold text-brand-600 hover:bg-brand-50 hover:text-brand-700"
              >
                Today
              </button>
            </div>

            {Boolean(value) && (
              <button
                type="button"
                onClick={clearDate}
                className="rounded px-2 py-1 font-medium text-slate-400 hover:bg-red-50 hover:text-red-600"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
